import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Users, Eye, Clock, Globe, Radio, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_LABELS = {
  "/": "Page d'accueil",
  "/admin": "Dashboard Admin",
  "/blog": "Blog",
  "/mentions-legales": "Mentions légales",
};

export function RealtimeTab() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const intervalRef = useRef(null);

  const fetchRealtime = async () => {
    try {
      const res = await axios.get(`${API}/admin/realtime`);
      setData(res.data);
      setHistory(prev => {
        const next = [...prev, { time: new Date(), count: res.data.active_now }];
        return next.slice(-30);
      });
    } catch {}
  };

  useEffect(() => {
    fetchRealtime();
    intervalRef.current = setInterval(fetchRealtime, 5000);
    return () => clearInterval(intervalRef.current);
  }, []);

  if (!data) return <div className="text-center py-20 text-[#666]">Chargement...</div>;

  const maxHistory = Math.max(...history.map(h => h.count), 1);

  return (
    <div className="space-y-6">
      {/* Live counter */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6 text-center relative overflow-hidden">
          <div className="absolute top-3 right-3 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-green-400 text-[10px] font-bold uppercase tracking-wider">Live</span>
          </div>
          <Users className="w-8 h-8 text-[#EFFF00] mx-auto mb-3" />
          <p className="text-5xl font-black font-['Outfit',sans-serif] text-white" data-testid="active-visitors-count">
            {data.active_now}
          </p>
          <p className="text-[#666] text-sm mt-2">Visiteur{data.active_now !== 1 ? "s" : ""} actif{data.active_now !== 1 ? "s" : ""}</p>
        </div>

        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6 text-center">
          <Eye className="w-8 h-8 text-blue-400 mx-auto mb-3" />
          <p className="text-5xl font-black font-['Outfit',sans-serif] text-white" data-testid="today-visitors-count">
            {data.today_total}
          </p>
          <p className="text-[#666] text-sm mt-2">Visiteurs aujourd'hui</p>
        </div>

        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6 text-center">
          <Globe className="w-8 h-8 text-purple-400 mx-auto mb-3" />
          <p className="text-5xl font-black font-['Outfit',sans-serif] text-white">
            {Object.keys(data.pages).length}
          </p>
          <p className="text-[#666] text-sm mt-2">Pages actives</p>
        </div>
      </div>

      {/* Activity sparkline */}
      {history.length > 1 && (
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <h3 className="text-sm font-bold text-[#A3A3A3] uppercase tracking-wider mb-4 flex items-center gap-2">
            <Radio className="w-4 h-4 text-green-400" />
            Activité en temps réel (5 dernières minutes)
          </h3>
          <div className="flex items-end gap-1 h-20" data-testid="realtime-chart">
            {history.map((h, i) => {
              const pct = Math.max(8, (h.count / maxHistory) * 100);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                  <span className="text-[#666] text-[9px] opacity-0 group-hover:opacity-100 transition-opacity">{h.count}</span>
                  <div
                    className="w-full rounded-t-sm transition-all duration-300"
                    style={{
                      height: `${pct}%`,
                      backgroundColor: i === history.length - 1 ? '#EFFF00' : '#333'
                    }}
                  />
                </div>
              );
            })}
          </div>
          <p className="text-[#444] text-[10px] mt-2 text-right">Rafraîchissement toutes les 5 secondes</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Pages breakdown */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <h3 className="text-sm font-bold text-[#A3A3A3] uppercase tracking-wider mb-4 flex items-center gap-2">
            <Eye className="w-4 h-4 text-blue-400" />
            Pages consultées
          </h3>
          {Object.keys(data.pages).length === 0 ? (
            <p className="text-[#666] text-sm">Aucun visiteur actif</p>
          ) : (
            <div className="space-y-3" data-testid="pages-breakdown">
              {Object.entries(data.pages)
                .sort((a, b) => b[1] - a[1])
                .map(([page, count], i) => {
                  const pct = (count / data.active_now) * 100;
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white text-sm font-medium truncate">
                          {PAGE_LABELS[page] || page}
                        </span>
                        <span className="text-[#EFFF00] font-bold text-sm">{count}</span>
                      </div>
                      <div className="w-full bg-[#1a1a1a] rounded-full h-2 overflow-hidden">
                        <div className="h-full bg-[#EFFF00] rounded-full transition-all duration-500"
                          style={{ width: `${Math.max(4, pct)}%` }} />
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Active visitors list */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <h3 className="text-sm font-bold text-[#A3A3A3] uppercase tracking-wider mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-green-400" />
            Sessions actives
          </h3>
          {data.visitors.length === 0 ? (
            <p className="text-[#666] text-sm">Aucun visiteur actif</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="visitors-list">
              {data.visitors.map((v, i) => {
                const lastSeen = new Date(v.last_seen);
                const secsAgo = Math.round((Date.now() - lastSeen.getTime()) / 1000);
                return (
                  <div key={i} className="flex items-center justify-between bg-[#0A0A0A] rounded-lg p-3 border border-[#1a1a1a]">
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${secsAgo < 15 ? 'bg-green-500' : 'bg-yellow-500'}`} />
                      <div>
                        <p className="text-white text-sm font-medium">
                          {PAGE_LABELS[v.page] || v.page}
                        </p>
                        {v.referrer && (
                          <p className="text-[#666] text-[10px] flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {(() => { try { return new URL(v.referrer).hostname; } catch { return v.referrer; } })()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-[#666] text-xs">
                      <Clock className="w-3 h-3" />
                      {secsAgo < 10 ? "maintenant" : secsAgo < 60 ? `${secsAgo}s` : `${Math.round(secsAgo / 60)}min`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
